import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from 'sonner';

export default function EventFormDialog({ open, onClose, event, group }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    is_recurring: false,
    recurrence_pattern: ''
  });

  const queryClient = useQueryClient();

  const { data: members = [] } = useQuery({
    queryKey: ['group-members', group?.id],
    queryFn: async () => {
      if (!group?.member_ids || group.member_ids.length === 0) return [];
      return base44.entities.Member.filter({ 
        id: { $in: group.member_ids },
        status: 'Ativo'
      });
    },
    enabled: !!group?.member_ids && group.member_ids.length > 0
  });

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        date: event.date || '',
        start_time: event.start_time || '',
        end_time: event.end_time || '',
        location: event.location || '',
        is_recurring: event.is_recurring || false,
        recurrence_pattern: event.recurrence_pattern || ''
      });
    } else {
      setFormData({
        title: '',
        description: '',
        date: '',
        start_time: '',
        end_time: '',
        location: '',
        is_recurring: false,
        recurrence_pattern: ''
      });
    }
  }, [event, open]);

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.GroupEvent.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-events'] });
      toast.success('Evento criado com sucesso!');
      onClose();
    },
    onError: () => toast.error('Erro ao criar evento')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.GroupEvent.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-events'] });
      toast.success('Evento atualizado!');
      onClose();
    },
    onError: () => toast.error('Erro ao atualizar evento')
  });

  const handleSubmit = () => {
    if (!formData.title || !formData.date) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    const attendees = members.map(m => ({
      member_id: m.id,
      member_name: m.full_name,
      status: 'pending'
    }));

    const eventData = {
      ...formData,
      group_id: group.id,
      group_name: group.name,
      attendees
    };

    if (event) {
      updateMutation.mutate({ id: event.id, data: eventData });
    } else {
      createMutation.mutate(eventData);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{event ? 'Editar Evento' : 'Novo Evento'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Reunião semanal do grupo"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Detalhes do evento..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Endereço ou local"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Início</label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Término</label>
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_recurring"
              checked={formData.is_recurring}
              onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700">
              Evento recorrente
            </label>
          </div>

          {formData.is_recurring && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Repetir</label>
              <Select
                value={formData.recurrence_pattern}
                onValueChange={(value) => setFormData({ ...formData, recurrence_pattern: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Semanalmente</SelectItem>
                  <SelectItem value="biweekly">Quinzenalmente</SelectItem>
                  <SelectItem value="monthly">Mensalmente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit} 
              className="flex-1"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {event ? 'Atualizar' : 'Criar Evento'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}